//@flow
import * as Immutable from 'immutable';

import { Competency } from 'common/models/flow/Competency';
import type { CompetencyModelType } from 'common/models/flow/Competency';

import { SkillId } from './SkillId';

import { RiderRating } from './RiderRating';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import I18nHelper from 'common/helpers/I18nHelper';
import TimeUtils from 'common/utils/TimeUtils';
import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import moment from 'moment-timezone';
const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalCompetency extends Competency {
    static fromJS(json: CompetencyModelType): InternalCompetency {
        const state: Object = Object.assign({}, json);
        state.rating = state.rating
            ? RiderRating.fromJS(state.rating)
            : state.rating;
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { rating } = this;

        if (rating) {
            rating = rating.setAllValidationErrors();
        }
        return this.setRating(rating);
    }

    @validate
    setJoiningDate(joiningDate: ?string): this {
        return super.setJoiningDate(joiningDate);
    }

    validateJoiningDate() {
        if (
            !DataUtils.isEmpty(this.joiningDate) &&
            !TimeUtils.format(this.joiningDate)
        ) {
            return nsTranslate('competency.invalidJoiningDate');
        }
    }

    @validate
    setRating(rating: ?RiderRating): this {
        return super.setRating(rating);
    }

    validateRating() {
        // let RiderRating model handle it
        return this.rating && this.rating.validateRating();
    }

    @validate
    setExperience(experience: ?string): this {
        return super.setExperience(experience);
    }

    validateExperience() {
        if (
            !DataUtils.isEmpty(this.experience) &&
            !NumberUtils.isInteger(this.experience)
        ) {
            return nsTranslate('competency.experienceNotAnInteger');
        }
        if (this.experience < 0) {
            return nsTranslate('competency.experienceLessThan0');
        }
    }

    getJoiningDateMoment() {
        return !DataUtils.isEmpty(this.joiningDate)
            ? moment(this.joiningDate)
            : null;
    }

    get rating(): ?RiderRating {
        return this._state.get('rating');
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedCompetency = super.defaultSanitize();
        if (sanitizedCompetency) {
            const sanitizedRating = sanitizedCompetency.rating?.defaultSanitize();
            return sanitizedCompetency.setRating(sanitizedRating);
        }
        return sanitizedCompetency;
    }

    static defaultInit({ props }): InternalCompetency {
        const rating = RiderRating.defaultInit({ props });
        return InternalCompetency.fromJS({}).setRating(rating);
    }
}

export { InternalCompetency as Competency };
